import { useEffect, useState } from "react";
import { Send, Users, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

const BLANK = { subject: "", body: "", audience: "all", audience_tier: "", audience_user_ids: [], send_email: true, send_in_panel: true };

export default function AffiliateBroadcast() {
  const [form, setForm] = useState(BLANK);
  const [history, setHistory] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [h, t, a] = await Promise.all([
        api.get("/admin/aff-broadcasts"),
        api.get("/admin/aff-commission-tiers"),
        api.get("/admin/aff-accounts"),
      ]);
      setHistory(h.data?.rows || []);
      setTiers(t.data?.rows || []);
      setAccounts(a.data?.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.subject || !form.body) return toast.error("Subject and body are required");
    setSending(true);
    try {
      const r = await api.post("/admin/aff-broadcast", form);
      toast.success(`Sent to ${r.data?.broadcast?.audience_count || 0} affiliates`);
      setForm(BLANK); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  const toggleUser = (uid) => {
    const exists = form.audience_user_ids.includes(uid);
    setForm({ ...form, audience_user_ids: exists ? form.audience_user_ids.filter(x => x !== uid) : [...form.audience_user_ids, uid] });
  };

  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">COMMUNICATION</div>
          <div className="text-3xl font-black ap-gold-text">Affiliate Broadcast</div>
          <div className="text-white/55 text-sm mt-1">Send email + in-panel notifications to your affiliate network.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <div className="ap-glass" style={{ padding: 18 }}>
            <Field label="Subject"><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="ap-input" data-testid="broadcast-subject" /></Field>
            <div className="mt-3">
              <Field label="Message"><textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="ap-input resize-none" data-testid="broadcast-body" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <Field label="Audience">
                <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="ap-input" data-testid="broadcast-audience">
                  <option value="all">All affiliates</option>
                  <option value="active">Active only</option>
                  <option value="tier">By tier</option>
                  <option value="custom">Custom list</option>
                </select>
              </Field>
              {form.audience === "tier" && (
                <Field label="Tier">
                  <select value={form.audience_tier} onChange={(e) => setForm({ ...form, audience_tier: e.target.value })} className="ap-input">
                    <option value="">— select —</option>
                    {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} />
                <span className="text-white text-[13px] font-semibold">Send Email</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.send_in_panel} onChange={(e) => setForm({ ...form, send_in_panel: e.target.checked })} />
                <span className="text-white text-[13px] font-semibold">Send In-Panel Notification</span>
              </label>
            </div>
            <button onClick={send} disabled={sending} className="ap-btn-gold mt-4 px-5 h-11 rounded-xl text-[13px] font-bold inline-flex items-center gap-2" data-testid="broadcast-send">
              <Send size={14} /> {sending ? "Sending…" : "Send Broadcast"}
            </button>
          </div>
        </div>
        <div className="lg:col-span-5">
          {form.audience === "custom" && (
            <div className="ap-glass mb-4" style={{ padding: 14 }}>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 mb-2">Select affiliates ({form.audience_user_ids.length})</div>
              <div className="max-h-[200px] overflow-y-auto pr-1 space-y-1">
                {accounts.map((a) => (
                  <label key={a.user_id} className="flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer hover:bg-white/5">
                    <input type="checkbox" checked={form.audience_user_ids.includes(a.user_id)} onChange={() => toggleUser(a.user_id)} />
                    <span className="text-white text-[12.5px] font-semibold">{a.username}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="ap-glass" style={{ padding: 14 }}>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 mb-2">History</div>
            <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
              {history.length === 0 ? (
                <div className="text-white/45 text-[12.5px]">No broadcasts yet.</div>
              ) : history.map((b) => (
                <div key={b.id} className="p-3 rounded-xl" style={{ background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="text-white font-bold text-[13px] truncate">{b.subject}</div>
                  <div className="text-white/45 text-[11px] mt-1">{(b.created_at || "").slice(0, 16).replace("T", " ")} • {b.audience}</div>
                  <div className="text-[11px] text-white/55 mt-1">✉︎ {b.delivered_email} / 🔔 {b.delivered_panel} / Audience {b.audience_count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">{label}</div>{children}</div>; }
